/**
 * Application providers: theme, Firebase session state and the query client.
 */

import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { onAuthStateChanged, type User } from 'firebase/auth'
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'

import { ApiError } from '@/shared/api/client'
import { ensureAnalytics, identify, resetAnalytics } from '@/shared/lib/analytics'
import { firebaseAuth, signInWith, signOut, type ProviderId } from './firebase'
import { ThemeProvider } from './theme'

interface SessionValue {
  /** null once resolved and nobody is signed in; undefined while resolving. */
  user: User | null | undefined
  ready: boolean
  signIn: (provider: ProviderId) => Promise<void>
  signOut: () => Promise<void>
}

const SessionContext = createContext<SessionValue | undefined>(undefined)

export function useSession(): SessionValue {
  const value = useContext(SessionContext)
  if (!value) throw new Error('useSession must be used inside <Providers>')
  return value
}

function buildQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // 401 and 403 will not resolve by asking again, and 409 is a real business
        // answer ("no seat available") rather than a transient fault. Retrying any
        // of them wastes the customer's time and multiplies our own rate limiting.
        retry: (failureCount, error) => {
          if (error instanceof ApiError) {
            if (error.status < 500 && error.status !== 429) return false
          }
          return failureCount < 2
        },
        refetchOnWindowFocus: false,
        staleTime: 15 * 1000,
      },
      mutations: {
        // A mutation retried automatically can double-charge or double-invite.
        retry: false,
      },
    },
  })
}

export function Providers({ children }: { children: ReactNode }) {
  const [queryClient] = useState(buildQueryClient)
  const [user, setUser] = useState<User | null | undefined>(undefined)
  /**
   * Who was signed in last time this fired: a uid, `null` for nobody, `undefined`
   * before the first resolution. Only the transition uid → null is a sign-out.
   */
  const previousUid = useRef<string | null | undefined>(undefined)

  useEffect(() => {
    // Browser-only: prerender imports this tree under Node and must not load PostHog.
    void ensureAnalytics()
  }, [])

  useEffect(() => {
    return onAuthStateChanged(firebaseAuth(), (next) => {
      setUser(next)
      const hadUser = Boolean(previousUid.current)
      previousUid.current = next?.uid ?? null
      if (hadUser && !next) {
        // Everything cached is scoped to the person who was signed in. Clearing on
        // sign-out is what stops a shared machine from showing the previous user's
        // workspace for the moment before the first refetch.
        //
        // Gated on there having BEEN a user, which is the whole point of the ref.
        // Firebase also fires this callback with `null` to say "resolved, nobody is
        // signed in" — the normal case for a visitor reading the landing page — and
        // clearing there wiped the public plan catalog the page had just fetched. The
        // query refetched, but into a cache entry that no longer had its observer, so
        // the plan cards on the landing stayed empty forever while /pricing (mounted
        // after auth had already resolved, so never cleared under it) showed prices
        // perfectly well. That asymmetry is what made it look like a copy problem.
        queryClient.clear()
        // Drop the identified person so the next browser session does not inherit them.
        resetAnalytics()
      } else if (next) {
        // Stable id = Firebase UID on web and API, so funnel events stitch together.
        identify(next.uid, {
          email: next.email ?? undefined,
          name: next.displayName ?? undefined,
        })
      }
    })
  }, [queryClient])

  const value = useMemo<SessionValue>(
    () => ({
      user,
      ready: user !== undefined,
      signIn: async (provider) => {
        await signInWith(provider)
      },
      signOut: async () => {
        await signOut()
        queryClient.clear()
        resetAnalytics()
      },
    }),
    [user, queryClient],
  )

  return (
    // Theme outermost: it owns an attribute on <html> and depends on nothing else,
    // so every tree below it — including an auth failure screen — is themed.
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
      </QueryClientProvider>
    </ThemeProvider>
  )
}
